import { chain, pickBy, sortBy } from 'lodash';
import filter from 'lodash/filter';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import reduce from 'lodash/reduce';

import { CommunicationQueue } from 'src/actions/communicationQueues/communicationQueuesResource';
import { CustomChannelConfiguration, Endpoint } from 'src/actions/endpoints/endpointsResource';
import { EndpointTypes } from 'src/models/Channels/endpointType';
import { formatPhone } from 'src/actions/shared/phonesHelper';
import { getTwitterUserName } from 'src/actions/shared/twitterHelper';

const EMPTY_GROUP = '//EMPTY//';

type CommunicationQueueWithEndpoint = CommunicationQueue & { endpoint: Partial<Endpoint> };
interface DropdownOption {
  label: string;
  value?: string | number;
  text?: string;
}
interface EndpointsDropdownOption extends DropdownOption {
  items?: (EndpointsDropdownOption | DropdownOption)[];
  type?: string;
}

interface ExceptionGroup {
  communicationQueues: string[];
  id: string;
  routingGroupId: string;
  slaSeconds: number | undefined;
}

export interface EndpointTypeSetting {
  type: EndpointTypes | string;
  communicationQueues: CommunicationQueueWithEndpoint[];
  defaultsIds: string[];
  endpointsOptions: EndpointsDropdownOption[];
  exceptions: string[];
  exceptionsGrouped: ExceptionGroup[];
}

/**
 * getSettingsForType returns for a specific endpointType a setting object:
 * {
 *  name: string;
 *  communicationQueues: CommunicationQueueWithEndpoint[]; // list of communication types for the type
 *  defaultsIds: string[]; // IDs list of comm queues with default sla and inbox
 *  endpointsOptions: (VoiceOptionGroup | { items?, label: string; value: string | number; text: string | undefined })[];
 *  exceptions: string[]; // IDs list of comm queues with different than default sla and inbox
 *  exceptionsGrouped: ExceptionGroup[];  // grops: {name, communicationQueues comm queue IDs with the same inbox and sla}
 * }
 */
export function getSettingsForType(
  endpointType: EndpointTypes | string, // CHAT,EMAIL,... or CUSTOM_CHANNEL:<id>,...
  { communicationQueues, endpointMap, ivrs, channelsConfig, accounts }
): EndpointTypeSetting {
  const type: EndpointTypes = endpointType.startsWith(EndpointTypes.CUSTOM_CHANNEL)
    ? EndpointTypes.CUSTOM_CHANNEL
    : (endpointType as EndpointTypes);
  const customChannelId = endpointType.split(':')[1];

  const commQueuesWithEndpointMap = map(communicationQueues, communicationQueue => ({
    ...communicationQueue,
    endpoint: endpointMap[communicationQueue.endpointId] ? endpointMap[communicationQueue.endpointId][0] : undefined,
  }));

  const communicationQueueWithEndpoint = commQueuesWithEndpointMap.filter(({ endpoint }) =>
    isEndpointBelongsToChannel(endpoint, type, customChannelId)
  );

  const groupedByRoutingGroupIdAndSla = chain(communicationQueueWithEndpoint)
    .groupBy((item: CommunicationQueueWithEndpoint) => `${item.routingGroupId}+++${item.slaSeconds}`)
    .value();

  const defaultSettings = {
    routingGroupId: channelsConfig?.defaultInbox,
    slaSeconds: channelsConfig?.defaultSLASeconds,
  };

  const defaults: string[] = [];
  const exceptions: string[] = [];

  forEach(communicationQueueWithEndpoint, (commQueue: CommunicationQueueWithEndpoint) => {
    const arr = hasEqualSettings(defaultSettings, commQueue) ? defaults : exceptions;
    arr.push(commQueue.id);
  });

  const groupedWithNoDefaultSettings = pickBy(
    groupedByRoutingGroupIdAndSla,
    commQueues => !hasEqualSettings(defaultSettings, commQueues[0])
  );

  const exceptionsGrouped = map(groupedWithNoDefaultSettings, (commQueues, groupName) => ({
    communicationQueues: sortBy(map(commQueues, ({ id }) => id)),
    id: groupName,
    routingGroupId: commQueues[0].routingGroupId,
    slaSeconds: commQueues[0].slaSeconds,
  }));

  return {
    ...defaultSettings,
    communicationQueues: communicationQueueWithEndpoint,
    defaultsIds: sortBy(defaults),
    endpointsOptions: getEndpointsOptions(type, communicationQueueWithEndpoint, ivrs, accounts),
    exceptions,
    exceptionsGrouped,
    type: endpointType,
  };
}

export function hasEqualSettings(
  defaultSettings: Partial<CommunicationQueue>,
  commQueue: Partial<CommunicationQueue>
): boolean {
  const isEqualRoutingGroupId = defaultSettings.routingGroupId === commQueue.routingGroupId;
  const slaSecondsFromDefaultSettings = defaultSettings.slaSeconds || 0;
  const slaSecondsFromCommQueue = commQueue.slaSeconds || 0;
  const isSlaEqual = slaSecondsFromDefaultSettings === slaSecondsFromCommQueue;

  return isEqualRoutingGroupId && isSlaEqual;
}

const isEndpointBelongsToChannel = (endpoint: Endpoint, endpointType: EndpointTypes, customChannelId?: string) =>
  endpointType === endpoint?.type &&
  customChannelId === (endpoint?.configuration as CustomChannelConfiguration)?.customChannelId;

export const includeHierarchicalOptions = ({
  items,
  includeIds,
}: {
  items: { label: string; value: string; group?: string }[];
  includeIds: string[];
}) => {
  const groupedItems: any[] = [];
  forEach(items, (item: any) => {
    if (item.type === 'group') {
      const filteredChildren = includeHierarchicalOptions({ items: item.items, includeIds });
      if (filteredChildren.length) {
        groupedItems.push({ ...item, items: filteredChildren });
      }
    } else if (includeIds.includes(item.value)) {
      groupedItems.push(item);
    }
  });
  return groupedItems;
};

function getEndpointsOptions(sectionType, communicationQueues, ivrs, accounts): EndpointsDropdownOption[] {
  if (sectionType === EndpointTypes.VOICE) {
    return getVoiceEndpointsOptions(communicationQueues, ivrs);
  }

  return map(communicationQueues, (communicationQueue: CommunicationQueueWithEndpoint) => {
    const address = [EndpointTypes.SMS, EndpointTypes.WHATSAPP].includes(sectionType as EndpointTypes)
      ? formatPhone(communicationQueue.endpoint.address || '')
      : communicationQueue.endpoint.address;

    let displayName;

    switch (sectionType as EndpointTypes) {
      case EndpointTypes.SMS:
        displayName = formatPhone(communicationQueue.endpoint.address || '');
        break;
      case EndpointTypes.TWITTER:
        displayName = getTwitterUserName(get(communicationQueue, 'endpoint', {}) as Endpoint);
        break;
      case EndpointTypes.FB_MESSENGER:
        const account = find(accounts, account => account.id === get(communicationQueue, 'endpoint.accountId'));
        displayName = get(account, 'content.name', address);
        break;
      case EndpointTypes.CUSTOM_CHANNEL:
        displayName = communicationQueue.endpoint.name;
        break;
      default:
        displayName = get(communicationQueue, 'endpoint.configuration.handle');
        break;
    }

    const label = `${displayName || address}${
      communicationQueue.routingSelector ? ', ' + communicationQueue.routingSelector : ''
    }`;

    return {
      value: communicationQueue.id,
      label,
      text: communicationQueue.endpoint.address,
    };
  });
}

function getVoiceEndpointsOptions(communicationQueues, ivrs) {
  const groups: Record<string, string[]> = reduce(
    communicationQueues,
    (acc: Record<string, string[]>, commQueueWithEndpoint: CommunicationQueueWithEndpoint) => {
      const ivrId = get(commQueueWithEndpoint.endpoint, 'configuration.initialIvr.ivrId', EMPTY_GROUP);
      if (!acc[ivrId]) {
        acc[ivrId] = [];
      }
      if (!acc[ivrId].includes(commQueueWithEndpoint.endpointId)) {
        acc[ivrId].push(commQueueWithEndpoint.endpointId);
      }
      return acc;
    },
    {}
  );

  const noIvr = groups[EMPTY_GROUP] || [];
  const result: EndpointsDropdownOption[] = filter(communicationQueues, item => noIvr.includes(item.endpointId)).map(
    item => ({
      label: `${formatPhone(item.endpoint.address || '')}${item.routingSelector ? ', ' + item.routingSelector : ''}`,
      value: item.id,
    })
  );

  const ivrsGroup: EndpointsDropdownOption = { items: [], type: 'group', label: 'IVRs' };

  const ivrsMap = reduce(
    ivrs,
    (acc, ivr) => {
      acc[ivr.id] = ivr;
      return acc;
    },
    {}
  );

  const commQueuesForEndpointMap: Record<string, CommunicationQueueWithEndpoint[]> = groupBy(
    communicationQueues,
    'endpointId'
  );

  forEach(groups, (group, ivrId: string) => {
    if (ivrId !== EMPTY_GROUP) {
      const ivr = ivrsMap[ivrId];
      const ivrName = ivr ? ivr.name || ivr.description || ivr.id : ivrId;
      const ivrGroup: EndpointsDropdownOption = { items: [], type: 'group', label: ivrName };

      forEach(group, endpointId => {
        const commQueuesForEndpoint = commQueuesForEndpointMap[endpointId];
        forEach(commQueuesForEndpoint, commQueue => {
          ivrGroup.items!.push({
            label: `${formatPhone(commQueue.endpoint.address || '')}${
              commQueue.routingSelector ? ', ' + commQueue.routingSelector : ''
            }`,
            value: commQueue.id,
            text: `${ivrGroup.label}, ${commQueue.endpoint.address}`,
          });
        });
      });

      ivrsGroup.items!.push(ivrGroup);
    }
  });

  result.push(ivrsGroup);

  return result;
}
